import { waistToHeightRatio } from '~/calculations/bodyComposition'
import type { WireConditionBand, WirePhaseType } from '~/contracts/gemini/request'
import type { ConditionBand } from '~/models/routeRecommendation'
import { isOpinion } from '~/models/rulesOpinion'
import { evaluateWeek } from '~/rules/engine'
import { deriveFacts } from '~/rules/facts'
import { bandFor } from '~/rules/route/engine'
import { DEFAULT_ROUTE_ENGINE_THRESHOLDS, DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { getProfile } from '~/storage/repositories/profileRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import type { Centimetres } from '~/types/units'
import { toCentimetres } from '~/types/units'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'
import type { PayloadSources } from './geminiPayload'
import { collectRulesInput } from './rulesService'

/**
 * The impure half of building a payload.
 *
 * `geminiPayload.ts` is a pure mapper and stays that way, so this is where the
 * clock and the database live — the same split `collectRulesInput` and
 * `evaluateWeek` already use, and for the same reason: the part that decides
 * what may be transmitted is testable without a fixture database, and the part
 * that reads storage has no opinions in it.
 */

/**
 * D-071's bands, in the wire's own words.
 *
 * A separate vocabulary on purpose (D-093 §2): the wire types are duplicated
 * from `models/` so an internal rename cannot silently change what goes over the
 * network. This is the one place the two are connected, and it is exhaustive, so
 * a fourth band added internally fails the build here rather than being sent as
 * something it is not.
 */
function wireBand(band: ConditionBand): WireConditionBand {
  switch (band) {
    case 'fatLossPriority':
      return 'above'
    case 'middle':
      return 'within'
    case 'leanEnough':
      return 'below'
  }
}

/**
 * The condition band, or `unknown`.
 *
 * Needs a waist and a height. **`unknown` is the honest answer when either is
 * missing, and is not a default standing in for one** — a cardio-only user has
 * no waist measurement, and reporting them as "within" would invent a reading
 * they never gave.
 */
function conditionBandFor(
  waistCm: number | undefined,
  heightCm: Centimetres | undefined,
): WireConditionBand {
  if (waistCm === undefined || heightCm === undefined) return 'unknown'

  /* The snapshot carries the waist as a plain number and the profile carries a
     branded `Centimetres`. Both are already canonical centimetres — the brand is
     what stops a display value in inches being passed here — but the conversion
     still validates, because `toCentimetres` rejects a non-finite or
     non-positive value and this is the boundary where a bad reading would become
     a condition band sent to a third party. */
  const waist = toCentimetres(waistCm)
  if (waist === undefined) return 'unknown'

  const ratio = waistToHeightRatio(waist, heightCm)
  if (ratio === undefined) return 'unknown'

  return wireBand(bandFor(ratio, DEFAULT_ROUTE_ENGINE_THRESHOLDS))
}

/**
 * Gather everything the builder needs.
 *
 * Returns a `Result`, so a storage failure is never reported as a payload that
 * happens to be missing things — the difference matters here more than usual,
 * because a payload silently short of its evidence would be reviewed as though
 * the evidence did not exist.
 */
export async function collectPayloadSources(
  db: SolaDatabase,
  now: Date,
): Promise<Result<PayloadSources, StorageError>> {
  const snapshot = await collectRulesInput(db, now)
  if (!snapshot.ok) return snapshot

  const project = await getActiveProject(db)
  if (!project.ok) return project

  const profile = await getProfile(db)
  if (!profile.ok) return profile

  /* No active project means no domain goals, which is a payload with an empty
     `domains` list rather than a failure. The Worker rejects that shape, so the
     user is told plainly rather than a request being sent that describes
     nobody. */
  const goals =
    project.value === undefined ? undefined : await listDomainGoals(db, project.value.id)
  if (goals !== undefined && !goals.ok) return goals

  const facts = deriveFacts(snapshot.value, DEFAULT_RULES_THRESHOLDS)
  const outcome = evaluateWeek(snapshot.value)
  const waistCm = snapshot.value.waist?.latestCm
  const heightCm = profile.value?.heightCm

  return ok({
    facts,
    adherence: snapshot.value.adherence,
    /* The engine's own confidence, read from the outcome rather than recomputed.
       The first draft of this line computed it from whether recovery was
       available — three words that would have put a second confidence rule in
       the codebase, disagreeing with D-066's the moment either moved, and
       visible only to a reviewer being told how much to trust the evidence.

       `low` when the engine produced no opinion at all: `PhaseRulesUnavailable`
       and its siblings are terminal states rather than recommendations (D-043),
       and the honest thing to tell a reviewer about a week Sola could not read
       is that confidence in it is low. */
    confidence: isOpinion(outcome) ? outcome.confidence : 'low',
    domainGoals: goals === undefined || !goals.ok ? [] : goals.value,
    constraints: {},
    ...(snapshot.value.phase === undefined
      ? {}
      : { phaseType: snapshot.value.phase.type as WirePhaseType }),
    conditionBand: conditionBandFor(waistCm, heightCm),
  })
}
